"use client";
import type { ProcessedSet } from "@workspace/types";
import { SET_STATUSES } from "@workspace/types";
import {
	Facepile as FacepileCore,
	type SetShowInteraction,
} from "@workspace/ui/components/facepile";
import { cn } from "@workspace/ui/lib/utils";
import { memo, useMemo } from "react";
import { getSetButtonContent } from "@/lib/business-logic/set-rendering";
import { getSetButtonClassName } from "@/lib/business-logic/set-styling";
import type { FollowerInteraction } from "@/lib/data/shared/facepile-cached";

interface FacepileProps {
	interactions: FollowerInteraction[];
	set?: ProcessedSet;
	festivalSlug?: string;
	maxAvatars?: number;
	size?: "sm" | "md";
	className?: string;
	allowHoverInModal?: boolean;
	disableLinks?: boolean;
}

/**
 * Status badge renderer for set/show interactions
 */
const statusBadgeRenderer = (
	interaction: SetShowInteraction,
	set?: ProcessedSet,
) => {
	// Get proper set status using existing logic
	const setStatus = (() => {
		// Direct status determination based on interaction fields
		// Following the same precedence as getSetStatus but using interaction data
		if (interaction.saw_live)
			return { status: SET_STATUSES.SAW_LIVE, hasStatus: true };
		if (interaction.saw_stream)
			return { status: SET_STATUSES.SAW_STREAM, hasStatus: true };
		if (interaction.must_see)
			return { status: SET_STATUSES.MUST_SEE, hasStatus: true };
		if (interaction.want_to_see)
			return { status: SET_STATUSES.SCHEDULED, hasStatus: true };

		return { status: SET_STATUSES.DEFAULT, hasStatus: false };
	})();

	if (!setStatus?.hasStatus) return null;

	return (
		<div
			className={cn(
				"flex w-full items-center justify-center gap-1 rounded px-1.5 py-0.5 text-xs",
				getSetButtonClassName(setStatus.status)
					.replace(/hover:bg-[^ ]+/g, "")
					.replace(/active:bg-[^ ]+/g, ""),
			)}
		>
			{set ? (
				getSetButtonContent(setStatus.status, set, false)
			) : (
				// Fallback for when we don't have a specific set (e.g., artist-festival card)
				<span className="text-xs">
					{setStatus.status === SET_STATUSES.SAW_LIVE && "Saw Live"}
					{setStatus.status === SET_STATUSES.SAW_STREAM && "Saw Stream"}
					{setStatus.status === SET_STATUSES.MUST_SEE && "Must See"}
					{setStatus.status === SET_STATUSES.SCHEDULED && "Want to See"}
				</span>
			)}
		</div>
	);
};

/**
 * Generic Facepile Component
 * Uses the new consolidated facepile system with 100% backward compatibility
 */
export const Facepile = memo(
	({
		interactions,
		set,
		festivalSlug,
		maxAvatars = 5,
		size = "sm",
		className,
		allowHoverInModal = false,
		disableLinks = false,
	}: FacepileProps) => {
		// Create a wrapped status badge renderer that includes the set
		const wrappedStatusBadgeRenderer = useMemo(
			() => (interaction: SetShowInteraction) =>
				statusBadgeRenderer(interaction, set),
			[set],
		);

		return (
			<FacepileCore
				interactions={interactions}
				set={set}
				festivalSlug={festivalSlug}
				maxAvatars={maxAvatars}
				size={size}
				className={className}
				allowHoverInModal={allowHoverInModal}
				disableLinks={disableLinks}
				statusBadgeRenderer={wrappedStatusBadgeRenderer}
			/>
		);
	},
);

Facepile.displayName = "Facepile";
